/**
 * Diagnostic accuracy from a 2 × 2 table of test result against the reference standard.
 *
 *                 Condition present   Condition absent
 *     Test +            TP                  FP
 *     Test −            FN                  TN
 *
 * Every proportion carries a Wilson 95% interval. Likelihood ratios and the diagnostic
 * odds ratio use the log method (Simel, Samsa & Matchar 1991): for LR+,
 * SE(ln LR+) = √(1/TP − 1/(TP + FN) + 1/FP − 1/(FP + TN)). An interval is only given
 * when the cells it needs are non-zero.
 */

import {
  type Interval,
  type ProportionEstimate,
  wilsonInterval,
  zFor,
} from "@/lib/tool-kit/stats-research/proportions";

export interface RatioWithInterval {
  value: number;
  interval: Interval | null;
}

export interface DiagnosticResult {
  total: number;
  sensitivity: ProportionEstimate | null;
  specificity: ProportionEstimate | null;
  ppv: ProportionEstimate | null;
  npv: ProportionEstimate | null;
  accuracy: ProportionEstimate | null;
  prevalence: ProportionEstimate | null;
  lrPositive: RatioWithInterval | null;
  lrNegative: RatioWithInterval | null;
  diagnosticOddsRatio: RatioWithInterval | null;
  f1: number;
  youden: number;
}

function logInterval(value: number, logSe: number, z: number): Interval {
  return {
    lower: value * Math.exp(-z * logSe),
    upper: value * Math.exp(z * logSe),
  };
}

/**
 * A ratio of two proportions, x/m ÷ y/s, with its log-method interval. Infinite (null)
 * when y is 0; zero, with no interval, when x is 0.
 */
function proportionRatio(
  x: number,
  m: number,
  y: number,
  s: number,
  z: number
): RatioWithInterval | null {
  if (y === 0) {
    return null;
  }
  const value = x / m / (y / s);
  if (x === 0) {
    return { value, interval: null };
  }
  const logSe = Math.sqrt(1 / x - 1 / m + 1 / y - 1 / s);
  return { value, interval: logInterval(value, logSe, z) };
}

function likelihoodRatios(
  tp: number,
  fp: number,
  fn: number,
  tn: number,
  z: number
): Pick<DiagnosticResult, "lrPositive" | "lrNegative" | "diagnosticOddsRatio"> {
  const diseased = tp + fn;
  const healthy = fp + tn;
  if (diseased === 0 || healthy === 0) {
    return { lrPositive: null, lrNegative: null, diagnosticOddsRatio: null };
  }
  let diagnosticOddsRatio: RatioWithInterval | null = null;
  if (tp > 0 && fp > 0 && fn > 0 && tn > 0) {
    const value = (tp * tn) / (fp * fn);
    const logSe = Math.sqrt(1 / tp + 1 / fp + 1 / fn + 1 / tn);
    diagnosticOddsRatio = { value, interval: logInterval(value, logSe, z) };
  }
  return {
    // LR+ = sensitivity / (1 − specificity); LR− = (1 − sensitivity) / specificity.
    lrPositive: proportionRatio(tp, diseased, fp, healthy, z),
    lrNegative: proportionRatio(fn, diseased, tn, healthy, z),
    diagnosticOddsRatio,
  };
}

export function diagnosticMeasures(
  tp: number,
  fp: number,
  fn: number,
  tn: number,
  level = 0.95
): DiagnosticResult {
  const total = tp + fp + fn + tn;
  const sensitivity = wilsonInterval(tp, tp + fn, level);
  const specificity = wilsonInterval(tn, tn + fp, level);
  const f1Denominator = 2 * tp + fp + fn;
  return {
    total,
    sensitivity,
    specificity,
    ppv: wilsonInterval(tp, tp + fp, level),
    npv: wilsonInterval(tn, tn + fn, level),
    accuracy: wilsonInterval(tp + tn, total, level),
    prevalence: wilsonInterval(tp + fn, total, level),
    ...likelihoodRatios(tp, fp, fn, tn, zFor(level)),
    f1: f1Denominator > 0 ? (2 * tp) / f1Denominator : Number.NaN,
    youden:
      sensitivity && specificity
        ? sensitivity.value + specificity.value - 1
        : Number.NaN,
  };
}

export interface PredictiveValues {
  ppv: number;
  npv: number;
  /** Per `population` people: the four cells of the expected table. */
  truePositives: number;
  falsePositives: number;
  falseNegatives: number;
  trueNegatives: number;
}

/**
 * Bayes' theorem: PPV = Se·π / (Se·π + (1 − Sp)(1 − π)) and
 * NPV = Sp(1 − π) / ((1 − Se)π + Sp(1 − π)), with the expected counts per `population`.
 */
export function predictiveValues(
  sensitivity: number,
  specificity: number,
  prevalence: number,
  population = 10_000
): PredictiveValues {
  const truePositives = sensitivity * prevalence * population;
  const falseNegatives = (1 - sensitivity) * prevalence * population;
  const trueNegatives = specificity * (1 - prevalence) * population;
  const falsePositives = (1 - specificity) * (1 - prevalence) * population;
  const positives = truePositives + falsePositives;
  const negatives = trueNegatives + falseNegatives;
  return {
    ppv: positives > 0 ? truePositives / positives : Number.NaN,
    npv: negatives > 0 ? trueNegatives / negatives : Number.NaN,
    truePositives,
    falsePositives,
    falseNegatives,
    trueNegatives,
  };
}
